import { useState } from "react";
import { Lock, Loader2, Users, ShieldCheck, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { Seo } from "@/components/Seo";

export default function Captains() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [spares, setSpares] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/captains/spares", { email: email.trim(), password });
      setSpares(data.spares || []);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Incorrect password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <Seo title="Captains Area | Quinte BCA Pool League" path="/captains" noindex />
      <PageHeader eyebrow="Team captains only" title="Captains Area" subtitle="Approved spare list — players available to fill in for league nights." />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {spares === null ? (
          <div className="max-w-sm mx-auto bg-white rounded-xl border border-[#E5E7EB] shadow-sm p-6" data-testid="captains-gate">
            <div className="flex items-center gap-2 mb-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#1E63C4]/10 text-[#1E63C4]"><Lock className="h-5 w-5" /></span>
              <div>
                <h2 className="font-heading font-bold">Sign in required</h2>
                <p className="text-xs text-[#555555]">Enter your email and the captains password to continue.</p>
              </div>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                data-testid="captains-email-input"
                className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
                autoFocus
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                data-testid="captains-password-input"
                className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
              />
              {error && <p className="text-sm text-[#E11D2A]" data-testid="captains-error">{error}</p>}
              <button type="submit" disabled={busy || !password || !email.trim()} data-testid="captains-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#1E63C4] text-white rounded-md px-4 py-2.5 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Checking…</> : "Enter"}
              </button>
            </form>
          </div>
        ) : (
          <div data-testid="captains-list">
            <div className="flex items-center gap-2 mb-5">
              <Users className="h-5 w-5 text-[#E11D2A]" />
              <h2 className="font-heading font-bold text-xl">Spare List <span className="font-normal text-sm text-[#555555]">({spares.length})</span></h2>
            </div>
            <div className="flex items-start gap-2.5 mb-5 rounded-md border border-amber-300 bg-amber-50 px-4 py-3" data-testid="spare-fargo-notice">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 font-medium">FargoRate listed are at time of spare list sign up — make sure to check to confirm current rating!</p>
            </div>
            {spares.length === 0 ? (
              <p className="text-[#555555]">No approved spares yet. Check back soon.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-[#E5E7EB] bg-white shadow-sm">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#0B0B0C] text-white text-left">
                      <th className="px-4 py-3 font-heading font-semibold">Name</th>
                      <th className="px-4 py-3 font-heading font-semibold">Telephone</th>
                      <th className="px-4 py-3 font-heading font-semibold">Fargo Rating</th>
                      <th className="px-4 py-3 font-heading font-semibold">Division</th>
                      <th className="px-4 py-3 font-heading font-semibold">BCA Sanctioned</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7EB]">
                    {spares.map((s) => (
                      <tr key={s.id} data-testid={`captains-spare-${s.id}`} className="hover:bg-[#F9FAF8]">
                        <td className="px-4 py-3 font-semibold text-[#111111]">{s.full_name}</td>
                        <td className="px-4 py-3"><a href={`tel:${s.phone}`} className="text-[#1E63C4] hover:underline">{s.phone}</a></td>
                        <td className="px-4 py-3 text-[#333333]">{s.fargo_rating || "—"}</td>
                        <td className="px-4 py-3 text-[#333333]">{s.divisions}</td>
                        <td className="px-4 py-3">
                          {s.bca_sanctioned ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5"><ShieldCheck className="h-3 w-3" /> Yes</span>
                          ) : (
                            <span className="inline-flex items-center text-xs font-semibold text-[#777] bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5">No</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
